/**
 * Pure helpers for the roles-flow institution popover.
 * Kept free of Angular/leader-line deps so they are unit-testable in isolation.
 */

export function escapeHtml(value: string | null | undefined): string {
  if (value == null) {
    return "";
  }
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Resolves the unit name attached to a role-institution:
 * 1. the roleInstitution's own unitName, if a non-empty string;
 * 2. otherwise the name of the institution unit whose id matches unitId;
 * 3. otherwise an empty string (no unit attached).
 */
export function resolveUnitName(roleInstitution: any, institution: any): string {
  const unitName = roleInstitution?.unitName;
  if (typeof unitName === "string" && unitName.trim()) {
    return unitName;
  }
  const unitId = roleInstitution?.unitId;
  if (unitId != null && unitId !== "" && institution?.units?.length) {
    const unit = institution.units.find((u) => u?.id != null && u.id.toString() === unitId.toString());
    if (unit?.name) {
      return unit.name;
    }
  }
  return "";
}

/**
 * Collects the distinct institution ids referenced by any role's roleInstitutions
 * that carry a unitId. These are the institutions whose child units must be loaded
 * so the popover can resolve unit names (the flow's institution list ships without
 * units in the Keycloak flow, and the role payload carries only unitId, not unitName).
 */
export function collectInstitutionIdsNeedingUnits(roles: any[]): string[] {
  const ids = new Set<string>();
  (roles || []).forEach((role) => {
    (role?.roleInstitutions || []).forEach((roleInstitution) => {
      const institutionId = roleInstitution?.institutionId;
      const unitId = roleInstitution?.unitId;
      if (institutionId != null && institutionId !== "" && unitId != null && unitId !== "") {
        ids.add(institutionId.toString());
      }
    });
  });
  return Array.from(ids);
}

/**
 * Builds the popover row for one institution, appending a muted unit span
 * when a unit is attached. Returns an HTML string (consumed via [innerHtml]).
 */
export function buildInstitutionRow(institution: any, roleInstitution: any): string {
  const name = escapeHtml(institution?.name);
  const unitName = resolveUnitName(roleInstitution, institution);
  const unitHtml = unitName
    ? ` <span class="flow-popover-unit">(${escapeHtml(unitName)})</span>`
    : "";
  return `<div>${name}${unitHtml}</div>`;
}
